/*
<summary>
   ProcessUsed is stored as a JSON-encoded array of Process names (e.g. ["Picking","Print"])
   Add/Edit Tool's Process Used field is a multi-select — these helpers convert that
   wire format to/from the { value, label } option shape FormikFormInputMultipleSelect
   (react-select) needs, and resolve it to display names for the grid.
   Developer: Aashish Singh, Created Date: 03-Sep-2026
</summary>
*/

import { IOption } from '../../models/ICommon';
import { resolveProcessLabel } from './forms/steps/step-one-tool-details';

export interface IProcessMultiSelectOption {
  value: number;
  label: string;
}

/**
* This function is used to parse ProcessUsed's raw wire value (a JSON array of process names,
* a legacy single plain process name, or null) into the { value, label } option list the
* multi-select control edits.
* @param raw: ProcessUsed as returned by the API
* @param processOptions: The full Process master option list (id + name)
*/
export const parseProcessUsedToOptions = (
  raw: string | null | undefined,
  processOptions: IOption[]
): IProcessMultiSelectOption[] => {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .map((name) => processOptions.find((option) => option.value === name))
        .filter((option): option is IOption => !!option)
        .map((option) => ({ value: option.id, label: resolveProcessLabel(option.value) }));
    }
  } catch {
    // Not JSON — fall through to the legacy single-name match below.
  }

  // Legacy data: ProcessUsed used to hold a single process name string directly (no array).
  const legacyMatch = processOptions.find((option) => option.value === raw);
  return legacyMatch
    ? [{ value: legacyMatch.id, label: resolveProcessLabel(legacyMatch.value) }]
    : [];
};

/**
* This function is used to turn the multi-select's selected options back into ProcessUsed's
* wire format — a JSON array of process names.
* @param selected: Currently-selected { value, label } options from the multi-select
* @param processOptions: The full Process master option list (id + name)
*/
export const stringifyProcessUsedNames = (
  selected: IProcessMultiSelectOption[] | null | undefined,
  processOptions: IOption[]
): string | null => {
  if (!selected || selected.length === 0) return null;
  const names = selected
    .map((option) => processOptions.find((p) => p.id === option.value)?.value)
    .filter((name): name is string => !!name);
  return names.length > 0 ? JSON.stringify(names) : null;
};

/**
* This function is used to resolve ProcessUsed's raw wire value into a comma-joined,
* per-locale-translated display string for the grid.
* @param raw: ProcessUsed as returned by the API
*/
export const resolveProcessUsedDisplayNames = (raw: string | null | undefined): string => {
  if (!raw) return '-';

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      const names = parsed.filter((name): name is string => !!name).map(resolveProcessLabel);
      return names.length > 0 ? names.join(', ') : '-';
    }
  } catch {
    // Not JSON — legacy plain name string, show as-is below. This is the normal/expected
    // path for any tool saved before ProcessUsed became a multi-select, not an error.
  }

  return raw;
};
